import React, { useEffect } from 'react';
import { FiX } from 'react-icons/fi';

/**
 * Modal — a flexible, composable dialog.
 *
 * Props
 * ─────
 * isOpen          {boolean}          Show/hide the modal
 * onClose         {function}         Called when backdrop or × is clicked
 * title           {string|ReactNode} Heading (optional)
 * description     {string|ReactNode} Subtitle below heading (optional)
 * children        {ReactNode}        Body — buttons, inputs, checkboxes, radios, dropdowns, text…
 * size            {'sm'|'md'|'lg'}   Max-width (default 'md')
 * showClose       {boolean}          Render × button in header (default true)
 * closeOnBackdrop {boolean}          Dismiss when clicking outside (default true)
 *
 * ─── Usage examples ─────────────────────────────────────────────────────────
 *
 * // 1. Simple confirmation with two action buttons
 * <Modal isOpen={open} onClose={close} title="Delete item?"
 *        description="This cannot be undone.">
 *   <div className="flex gap-2 mt-4">
 *     <button onClick={close}>Cancel</button>
 *     <button onClick={doDelete}>Delete</button>
 *   </div>
 * </Modal>
 *
 * // 2. Checkbox option
 * <Modal isOpen={open} onClose={close} title="Remove designer">
 *   <label className="flex items-center gap-2 mt-2">
 *     <input type="checkbox" checked={keep} onChange={e => setKeep(e.target.checked)} />
 *     Keep completed state
 *   </label>
 *   <div className="flex gap-2 mt-4">
 *     <button onClick={close}>Cancel</button>
 *     <button onClick={confirm}>Remove</button>
 *   </div>
 * </Modal>
 *
 * // 3. Radio picker
 * <Modal isOpen={open} onClose={close} title="Choose priority">
 *   {['low','medium','high'].map(p => (
 *     <label key={p} className="flex items-center gap-2">
 *       <input type="radio" value={p} checked={priority === p}
 *              onChange={() => setPriority(p)} />
 *       {p}
 *     </label>
 *   ))}
 *   <button onClick={close}>Confirm</button>
 * </Modal>
 *
 * // 4. Announcement (close-only)
 * <Modal isOpen={open} onClose={close} title="Heads up!">
 *   <p>Your changes were saved.</p>
 *   <button className="mt-4 w-full" onClick={close}>Got it</button>
 * </Modal>
 *
 * // 5. Dropdown inside modal
 * <Modal isOpen={open} onClose={close} title="Select team">
 *   <select value={team} onChange={e => setTeam(e.target.value)}>
 *     <option value="">— pick one —</option>
 *     <option value="design">Design</option>
 *   </select>
 *   <button onClick={close}>Done</button>
 * </Modal>
 */

const Modal = ({
    isOpen,
    onClose,
    title,
    description,
    children,
    size = 'md',
    showClose = true,
    closeOnBackdrop = true,
    customPanelStyle = ""
}) => {
    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const maxW = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-lg' }[size] ?? 'max-w-md';
    const hasHeader = title || description || showClose;

    return (
        <div className="mobile-dialog-shell fixed inset-0 z-50 top-0 !mt-0 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={closeOnBackdrop ? onClose : undefined}
            />

            {/* Panel */}
            <div className={`relative bg-white dark:bg-zinc-800 rounded-xl shadow-2xl w-full ${maxW} ${customPanelStyle}`}>

                {/* Header */}
                {hasHeader && (
                    <div className="flex items-start justify-between px-5 pt-5 pb-1">
                        <div className="flex-1 min-w-0 pr-2">
                            {title && (
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-snug">
                                    {title}
                                </h3>
                            )}
                            {description && (
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    {description}
                                </p>
                            )}
                        </div>
                        {showClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-shrink-0 p-1 -mr-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded-lg transition-colors"
                                aria-label="Close"
                            >
                                <FiX className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}

                {/* Body */}
                <div className={`px-5 pb-5 ${hasHeader ? 'pt-4' : 'pt-5'}`}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Modal;
